import { EPermissions } from '@enums/permissions.enum';

/**
 * Always included in every custom role's permissions, regardless of which sections/toggles are
 * enabled - baseline access every group member needs (read/update their own profile, switch
 * groups, submit feedback, read news, manage their own uploaded images, create events) that isn't
 * presented as a toggle in the Add/Edit Custom Role form. The backend does not add these
 * automatically - create_custom_role/update_custom_role store exactly the permissions list they're
 * sent - so this list must always be folded into that payload, see buildRolePermissions below.
 */
export const DEFAULT_ROLE_PERMISSIONS: EPermissions[] = [
  EPermissions.userReadOwn,
  EPermissions.userUpdateOwn,
  EPermissions.userGroupReadOwn,
  EPermissions.userGroupSwitchOwn,
  EPermissions.userFeedbackCreate,
  EPermissions.newsReadOwn,
  EPermissions.imagesCreate,
  EPermissions.imagesReadOwn,
  EPermissions.imagesDeleteOwn,
  EPermissions.eventsCreate,
];

export interface IRolePermissionToggle {
  /** Unique within its section - combined with the section key as "sectionKey.toggleKey" to form
   * the flat key used by buildRolePermissions/deriveEnabledToggleKeys. */
  key: string;
  labelKey: string;
  permissions: EPermissions[];
}

export interface IRolePermissionSection {
  key: string;
  titleKey: string;
  descriptionKey: string;
  toggles: IRolePermissionToggle[];
}

const KEY_PREFIX = 'pages.settings.userManagement.rolePermissions';

/**
 * The Add/Edit/View Custom Role form's four sections, each a fixed set of named toggles - some
 * toggles map to a single backend permission, others bundle several together under one switch
 * (e.g. "View" folds every campaign read-ish permission into one toggle). This is the single
 * source of truth for both directions: building the permissions payload to send, and deriving
 * which toggles should show as "on" for an existing role's saved permissions.
 */
export const ROLE_PERMISSION_SECTIONS: IRolePermissionSection[] = [
  {
    descriptionKey: `${KEY_PREFIX}.campaigns.description`,
    key: 'campaigns',
    titleKey: `${KEY_PREFIX}.campaigns.title`,
    toggles: [
      { key: 'create', labelKey: `${KEY_PREFIX}.toggles.create`, permissions: [EPermissions.campaignCreate] },
      {
        key: 'view',
        labelKey: `${KEY_PREFIX}.toggles.view`,
        permissions: [
          EPermissions.campaignReadOwn,
          EPermissions.campaignAnalyticsReadOwn,
          EPermissions.manualFindingStatusReadOwn,
          EPermissions.currencyRead,
          EPermissions.campaignAnalyticsManualMatchingStatusReadOwn,
          EPermissions.campaignAnalyticsManualStatusReadOwn,
          EPermissions.campaignValidate,
        ],
      },
      { key: 'edit', labelKey: `${KEY_PREFIX}.toggles.edit`, permissions: [EPermissions.campaignUpdateOwn] },
      { key: 'delete', labelKey: `${KEY_PREFIX}.toggles.delete`, permissions: [EPermissions.campaignDeleteOwn] },
      {
        key: 'manualDeliverablesStatus',
        labelKey: `${KEY_PREFIX}.campaigns.manualDeliverablesStatus`,
        permissions: [
          EPermissions.campaignAnalyticsManualStatusCreate,
          EPermissions.campaignAnalyticsManualStatusUpdateOwn,
        ],
      },
      {
        key: 'manualMatchingStatus',
        labelKey: `${KEY_PREFIX}.campaigns.manualMatchingStatus`,
        permissions: [
          EPermissions.campaignAnalyticsManualMatchingStatusCreate,
          EPermissions.campaignAnalyticsManualMatchingStatusUpdateOwn,
        ],
      },
      {
        key: 'findingDetection',
        labelKey: `${KEY_PREFIX}.campaigns.findingDetection`,
        permissions: [EPermissions.manualFindingStatusCreate, EPermissions.manualFindingStatusUpdateOwn],
      },
    ],
  },
  {
    descriptionKey: `${KEY_PREFIX}.creators.description`,
    key: 'creators',
    titleKey: `${KEY_PREFIX}.creators.title`,
    toggles: [
      {
        key: 'create',
        labelKey: `${KEY_PREFIX}.toggles.create`,
        permissions: [EPermissions.creatorCreate, EPermissions.creatorValidate],
      },
      {
        key: 'view',
        labelKey: `${KEY_PREFIX}.toggles.view`,
        permissions: [
          EPermissions.creatorReadOwn,
          EPermissions.creatorAnalyticsReadOwn,
          EPermissions.creatorFollowersRead,
        ],
      },
      { key: 'edit', labelKey: `${KEY_PREFIX}.toggles.edit`, permissions: [EPermissions.creatorUpdateOwn] },
      { key: 'delete', labelKey: `${KEY_PREFIX}.toggles.delete`, permissions: [EPermissions.creatorDeleteOwn] },
      { key: 'auth', labelKey: `${KEY_PREFIX}.creators.auth`, permissions: [EPermissions.creatorAuthenticate] },
      {
        key: 'invitation',
        labelKey: `${KEY_PREFIX}.creators.invitation`,
        permissions: [EPermissions.creatorInvitationCreate],
      },
    ],
  },
  {
    descriptionKey: `${KEY_PREFIX}.clients.description`,
    key: 'clients',
    titleKey: `${KEY_PREFIX}.clients.title`,
    toggles: [
      { key: 'create', labelKey: `${KEY_PREFIX}.toggles.create`, permissions: [EPermissions.customerCreate] },
      { key: 'view', labelKey: `${KEY_PREFIX}.toggles.view`, permissions: [EPermissions.customerReadOwn] },
      { key: 'edit', labelKey: `${KEY_PREFIX}.toggles.edit`, permissions: [EPermissions.customerUpdateOwn] },
      { key: 'delete', labelKey: `${KEY_PREFIX}.toggles.delete`, permissions: [EPermissions.customerDeleteOwn] },
    ],
  },
  {
    descriptionKey: `${KEY_PREFIX}.userManagement.description`,
    key: 'userManagement',
    titleKey: `${KEY_PREFIX}.userManagement.title`,
    toggles: [
      {
        key: 'manage',
        labelKey: `${KEY_PREFIX}.userManagement.manage`,
        permissions: [
          EPermissions.userChangeRoleOwn,
          EPermissions.campaignSubUserInvitationReadOwn,
          EPermissions.campaignSubUserInvitationCreate,
          EPermissions.userGroupDeleteMemberOwn,
          EPermissions.roleCreate,
          EPermissions.roleReadOwn,
          EPermissions.roleUpdateOwn,
          EPermissions.roleDeleteOwn,
          EPermissions.teamCreate,
          EPermissions.teamReadOwn,
          EPermissions.teamUpdateOwn,
          EPermissions.teamDeleteOwn,
          EPermissions.userChangeTeamOwn,
        ],
      },
    ],
  },
];

/** Flat "sectionKey.toggleKey" identifier for a toggle, used as the Set key both here and in the
 * form component's own enabled-toggles state. */
export function toggleKeyFor(sectionKey: string, toggleKey: string): string {
  return `${sectionKey}.${toggleKey}`;
}

/** Every permission covered by some toggle in ROLE_PERMISSION_SECTIONS - used by
 * buildRolePermissions to tell an existing role's "unmapped" permissions (never represented by any
 * toggle at all, so this form can never legitimately drop them) apart from its "mapped" ones
 * (governed by a toggle, and only droppable the way buildRolePermissions handles below). */
function getMappedPermissions(): Set<string> {
  return new Set<string>(
    ROLE_PERMISSION_SECTIONS.flatMap((section) => section.toggles.flatMap((toggle) => toggle.permissions)),
  );
}

/** Builds the full permissions list to send to POST/PUT /roles for a given set of enabled toggle
 * keys (each "sectionKey.toggleKey", see toggleKeyFor) - always folds in DEFAULT_ROLE_PERMISSIONS
 * since the backend does not add those on its own.
 *
 * `existingPermissions` (an existing role's own saved `permissions`, omitted entirely in 'create'
 * mode) and `touchedToggleKeys` (which toggles the user has actually flipped this session, see
 * add-custom-role-dialog.component's `touchedToggleKeys`) exist to avoid silently dropping
 * permissions this form never gave the user a real chance to change:
 * - A permission not covered by ANY toggle at all is always preserved as-is - there's no control
 *   in this form that could represent a decision to drop it.
 * - A toggle's bundle can be only *partially* present on an existing role (deriveEnabledToggleKeys
 *   then derives it as "off", since that only requires every permission to be present). If the
 *   user never actually touches that toggle, its current "off" enabled-state is an artifact of
 *   that partial data, not a real decision to remove it - so the existing subset is preserved
 *   as-is instead. Only once the user actually flips a toggle does its current on/off state get
 *   treated as an explicit, all-or-nothing decision for that whole bundle. */
export function buildRolePermissions(
  enabledToggleKeys: ReadonlySet<string>,
  touchedToggleKeys: ReadonlySet<string> = new Set<string>(),
  existingPermissions: readonly string[] = [],
): string[] {
  const existingPermissionSet = new Set(existingPermissions);
  const mappedPermissions = getMappedPermissions();
  const permissions = new Set<string>(DEFAULT_ROLE_PERMISSIONS);

  for (const permission of existingPermissionSet) {
    if (!mappedPermissions.has(permission)) {
      permissions.add(permission);
    }
  }

  for (const section of ROLE_PERMISSION_SECTIONS) {
    for (const toggle of section.toggles) {
      const key = toggleKeyFor(section.key, toggle.key);

      if (enabledToggleKeys.has(key)) {
        for (const permission of toggle.permissions) {
          permissions.add(permission);
        }

        continue;
      }

      if (touchedToggleKeys.has(key)) {
        // Explicitly turned off - drop the whole bundle, even a partially-saved one.
        continue;
      }

      // Never touched, and only "off" because the bundle wasn't fully present to begin with -
      // keep whichever of its permissions the role already had.
      for (const permission of toggle.permissions) {
        if (existingPermissionSet.has(permission)) {
          permissions.add(permission);
        }
      }
    }
  }

  return [...permissions];
}

/** Derives which toggles are "on" for an existing role's raw `permissions` list (as returned by
 * GET /roles) - a toggle is on only when every one of its mapped permissions is present. Used to
 * pre-fill the Edit/View Permissions form from an existing role. */
export function deriveEnabledToggleKeys(rolePermissions: readonly string[]): Set<string> {
  const permissionSet = new Set(rolePermissions);
  const enabledToggleKeys = new Set<string>();

  for (const section of ROLE_PERMISSION_SECTIONS) {
    for (const toggle of section.toggles) {
      if (toggle.permissions.every((permission) => permissionSet.has(permission))) {
        enabledToggleKeys.add(toggleKeyFor(section.key, toggle.key));
      }
    }
  }

  return enabledToggleKeys;
}
